import { Cartesian3, Ray } from "cesium";
const TRIANGLE_TOLERANCE = 0.001;
/**
 *
 * @param geometry
 * @param plane
 * @param intersectedLines intersected lines are stored for debugging purposes
 * @returns
 */
export function intersectGeometryByPlane(geometry, plane) {
    const result = [];
    const ray = new Ray(new Cartesian3(), new Cartesian3(1, 0, 0));
    const dir = new Cartesian3();
    const ndir = new Cartesian3();
    const p1 = new Cartesian3();
    const p2 = new Cartesian3();
    const p3 = new Cartesian3();
    const intersectLineSegmentToPlane = (p1, p2) => {
        if (!Cartesian3.equals(p1, p2)) {
            Cartesian3.subtract(p2, p1, dir);
            Cartesian3.normalize(dir, dir);
            Cartesian3.clone(dir, ray.direction);
            Cartesian3.negate(dir, ndir);
            Cartesian3.normalize(ndir, ndir);
            Cartesian3.add(p1, Cartesian3.multiplyByScalar(ndir, TRIANGLE_TOLERANCE, new Cartesian3()), ray.origin);
            const hit = plane.raycast(ray);
            if (hit) {
                const hitdist = Cartesian3.distance(hit, ray.origin);
                if (hitdist !== 0 &&
                    hitdist < Cartesian3.distance(p1, p2) + 2 * TRIANGLE_TOLERANCE) {
                    return hit.clone();
                }
            }
        }
    };
    for (let i = 0; i < geometry.indices.length; i += 3) {
        Cartesian3.fromArray(geometry.vertices, geometry.indices[i] * 3, p1);
        Cartesian3.fromArray(geometry.vertices, geometry.indices[i + 1] * 3, p2);
        Cartesian3.fromArray(geometry.vertices, geometry.indices[i + 2] * 3, p3);
        const hit1 = intersectLineSegmentToPlane(p1, p2);
        const hit2 = intersectLineSegmentToPlane(p1, p3);
        const hit3 = intersectLineSegmentToPlane(p2, p3);
        if (hit1) {
            result.push(hit1);
        }
        if (hit2) {
            result.push(hit2);
        }
        if (hit3) {
            result.push(hit3);
        }
    }
    return result;
}
/**
 * based on: http://www.blackpawn.com/texts/pointinpoly/default.html
 * Calculates the barycentric coordinates of a point relative to a triangle defined by three vertices in 3D space.
 * Barycentric coordinates are used to express a point inside a triangle as a combination of the triangle's vertices.
 *
 * @param {Cartesian3} point - The point to compute the barycentric coordinates for.
 * @param {Cartesian3} a - The first vertex of the triangle.
 * @param {Cartesian3} b - The second vertex of the triangle.
 * @param {Cartesian3} c - The third vertex of the triangle.
 * @returns {Cartesian3|undefined} - Returns a Cartesian3 object containing the barycentric coordinates (u, v, w),
 * where w = 1 - u - v. Returns `undefined` if the triangle is degenerate or the point lies outside the triangle.
 *
 * @example
 * const point = new Cartesian3(1, 1, 0);
 * const a = new Cartesian3(0, 0, 0);
 * const b = new Cartesian3(2, 0, 0);
 * const c = new Cartesian3(0, 2, 0);
 *
 * const barycoord = getBarycoord(point, a, b, c);
 * if (barycoord !== undefined) {
 *   console.log(barycoord); // Cartesian3 object representing barycentric coordinates
 * } else {
 *   console.log("Point is outside the triangle or triangle is degenerate");
 * }
 */
function getBarycoord(point, a, b, c) {
    const v0 = Cartesian3.subtract(c, a, new Cartesian3());
    const v1 = Cartesian3.subtract(b, a, new Cartesian3());
    const v2 = Cartesian3.subtract(point, a, new Cartesian3());
    const dot00 = Cartesian3.dot(v0, v0);
    const dot01 = Cartesian3.dot(v0, v1);
    const dot02 = Cartesian3.dot(v0, v2);
    const dot11 = Cartesian3.dot(v1, v1);
    const dot12 = Cartesian3.dot(v1, v2);
    const denom = dot00 * dot11 - dot01 * dot01;
    // collinear or singular triangle
    if (denom === 0) {
        return undefined;
    }
    const invDenom = 1 / denom;
    const u = (dot11 * dot02 - dot01 * dot12) * invDenom;
    const v = (dot00 * dot12 - dot01 * dot02) * invDenom;
    // barycentric coordinates must always sum to 1
    return new Cartesian3(1 - u - v, v, u);
}
/**
 * Determines if a point is inside a triangle defined by three vertices in 3D space.
 * The function uses barycentric coordinates to determine whether the point lies within the bounds of the triangle.
 * @param {Cartesian3} point - The point to test.
 * @param {Cartesian3} a - The first vertex of the triangle.
 * @param {Cartesian3} b - The second vertex of the triangle.
 * @param {Cartesian3} c - The third vertex of the triangle.
 * @returns {boolean} - Returns `true` if the point is inside the triangle, `false` otherwise.
 *
 * @example
 * const point = new Cartesian3(1, 1, 0);
 * const a = new Cartesian3(0, 0, 0);
 * const b = new Cartesian3(2, 0, 0);
 * const c = new Cartesian3(0, 2, 0);
 * const isInside = isPointInTriangle(point, a, b, c);
 * console.log(isInside); // true if the point is inside the triangle, false otherwise.
 */
export function isPointInTriangle(point, a, b, c) {
    // if the triangle is degenerate then we can't contain a point
    const barycentric = getBarycoord(point, a, b, c);
    if (!barycentric) {
        return false;
    }
    return (barycentric.x >= 0 &&
        barycentric.y >= 0 &&
        barycentric.x + barycentric.y <= 1);
}
//# sourceMappingURL=geometry-utility.js.map